import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { cn } from '../Button';

export interface SearchableSelectOption {
  value: string;
  label: string;
  description?: string;
}

interface SearchableSelectProps {
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  multiple?: boolean;
  closeOnSelect?: boolean;
  options: SearchableSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  label,
  placeholder = 'Selecionar...',
  searchPlaceholder = 'Buscar...',
  emptyMessage = 'Nenhuma opcao encontrada.',
  multiple = true,
  closeOnSelect = false,
  options,
  value,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (!containerRef.current?.contains(target) && !dropdownRef.current?.contains(target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updateDropdownPosition = () => {
    const button = buttonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const viewportMargin = 12;
    const preferredMaxHeight = 320;
    const spaceBelow = window.innerHeight - rect.bottom - viewportMargin;
    const spaceAbove = rect.top - viewportMargin;
    const shouldRenderAbove = spaceBelow < 220 && spaceAbove > spaceBelow;
    const availableHeight = shouldRenderAbove ? spaceAbove : spaceBelow;
    const maxHeight = Math.max(180, Math.min(preferredMaxHeight, availableHeight));
    const top = shouldRenderAbove ? rect.top - maxHeight - 8 : rect.bottom + 8;
    const left = Math.max(viewportMargin, Math.min(rect.left, window.innerWidth - rect.width - viewportMargin));

    setDropdownPosition({
      top,
      left,
      width: rect.width,
      maxHeight,
    });
  };

  useLayoutEffect(() => {
    if (!isOpen) {
      setDropdownPosition(null);
      return;
    }

    updateDropdownPosition();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleReflow = () => updateDropdownPosition();
    window.addEventListener('resize', handleReflow);
    window.addEventListener('scroll', handleReflow, true);

    return () => {
      window.removeEventListener('resize', handleReflow);
      window.removeEventListener('scroll', handleReflow, true);
    };
  }, [isOpen]);

  const selectedOptions = useMemo(
    () => options.filter((option) => value.includes(option.value)),
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery) {
      return options;
    }

    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(normalizedQuery) ||
        option.description?.toLowerCase().includes(normalizedQuery)
    );
  }, [options, query]);

  const toggleOption = (optionValue: string) => {
    if (!multiple) {
      const nextValue = value.includes(optionValue) ? [] : [optionValue];
      onChange(nextValue);
      if (closeOnSelect) {
        setIsOpen(false);
        setQuery('');
      }
      return;
    }

    if (value.includes(optionValue)) {
      onChange(value.filter((item) => item !== optionValue));
      return;
    }

    onChange([...value, optionValue]);
  };

  const removeValue = (optionValue: string) => {
    onChange(value.filter((item) => item !== optionValue));
  };

  return (
    <div ref={containerRef} className="w-full space-y-1.5">
      {label && <label className="ml-1 text-sm font-medium text-gray-700">{label}</label>}

      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen((current) => !current)}
          className={cn(
            'flex min-h-[48px] min-w-0 w-full items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-2 text-left text-sm transition-all',
            'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary'
          )}
        >
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 pr-4">
            {selectedOptions.length > 0 ? (
              selectedOptions.map((option) => (
                <span
                  key={option.value}
                  className="inline-flex items-center gap-1 rounded-full bg-primary-light px-2.5 py-1 text-xs font-semibold text-primary"
                >
                  {option.label}
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(event) => {
                      event.stopPropagation();
                      removeValue(option.value);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                        removeValue(option.value);
                      }
                    }}
                  >
                    <X size={12} />
                  </span>
                </span>
              ))
            ) : (
              <span className="text-gray-400">{placeholder}</span>
            )}
          </div>
          <ChevronDown size={18} className={cn('shrink-0 text-gray-400 transition-transform', isOpen && 'rotate-180')} />
        </button>

        {isOpen && dropdownPosition &&
          createPortal(
            <div
              ref={dropdownRef}
              className="fixed z-[110] rounded-2xl border border-gray-200 bg-white p-3 shadow-xl"
              style={{
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
              }}
            >
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={searchPlaceholder}
                  className="h-11 w-full rounded-xl border border-gray-200 bg-slate-50 pl-10 pr-3 text-sm outline-none transition-all focus:border-primary focus:bg-white"
                />
              </div>

              <div className="mt-3 space-y-1 overflow-y-auto" style={{ maxHeight: dropdownPosition.maxHeight - 72 }}>
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option) => {
                    const isSelected = value.includes(option.value);

                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => toggleOption(option.value)}
                        className={cn(
                          'flex w-full items-center justify-between rounded-xl px-3 py-2 text-left transition-colors',
                          isSelected ? 'bg-primary-light text-primary' : 'hover:bg-slate-50 text-slate-700'
                        )}
                      >
                        <div>
                          <p className="text-sm font-medium">{option.label}</p>
                          {option.description && <p className="text-xs text-gray-500">{option.description}</p>}
                        </div>
                        {isSelected && <Check size={16} />}
                      </button>
                    );
                  })
                ) : (
                  <p className="px-2 py-3 text-sm text-gray-500">{emptyMessage}</p>
                )}
              </div>
            </div>,
            document.body
          )}
      </div>
    </div>
  );
};

export default SearchableSelect;
